import React, { useState, useEffect } from 'react';
import {
  Modal,
  Pressable,
  Keyboard,
  View,
  StyleSheet,
  Image,
  TouchableWithoutFeedback,
  TextInput,
  SafeAreaView,
  ScrollView,
  Button,
  ActivityIndicator,
} from 'react-native';
import { ImageViewer } from 'react-native-image-zoom-viewer';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import CustomText from '../components/CustomText';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const MonetaImageGallery = (props) => {
  const { t } = useTranslation('reports');
  const { isWeb } = useResponsiveStyles();
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);

  const renderLoading = () => {
    return <ActivityIndicator color={'white'} size={'large'} />;
  };

  // Expects values to be pass as an object with source and style.
  // This is required to work with ImageViewer.
  const renderImage = ({ source, style }) => {
    return (
      <Image
        source={{ uri: source?.uri, priority: 'high' }}
        style={style}
        resizeMode="contain"
        indicator={renderLoading}
      />
    );
  };

  const getDisplayImage = (index) => {
    if (props.images && props.images.length > index) {
      return renderImage({ source: { uri: props.images[index] }, style: { flex: 1 } });
    } else {
      return <View></View>;
    }
  };

  const decrementImage = () => {
    if (props.images && props.images.length > 0) {
      setGalleryIndex((galleryIndex + props.images.length - 1) % props.images.length);
    }
  };

  const incrementImage = () => {
    if (props.images && props.images.length > 0) {
      setGalleryIndex((galleryIndex + 1) % props.images?.length);
    }
  };

  const deleteImage = () => {
    const newImages = props.images.filter((value, index) => {
      return index != galleryIndex;
    });
    props.setImages(() => newImages);

    //Special case for deleting last element
    //If deleting first or middle element, index remains the same
    //When list is empty, index should always end up being 0, so no need to worry about that either
    if (newImages.length >= galleryIndex && galleryIndex > 0) {
      setGalleryIndex(newImages.length - 1);
    }
  };

  if (isWeb) {
    return (
      props?.images &&
      props?.images.length > 0 && (
        <View style={{ borderRadius: 16, borderColor: AppStyles.colorSet.cardBorder, borderWidth: 1, marginTop: 15, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.04, shadowRadius: 8, elevation: 2, backgroundColor: AppStyles.colorSet.cardBackground }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12 }}>
            <CustomText
              font="medium"
              size={14}
              color={AppStyles.colorSet.textSecondary}
            >
              {t('gallery.indexOfTotal', { current: galleryIndex + 1, total: props.images.length })}
            </CustomText>

            {props.images.length > 1 && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Pressable
                  style={{
                    width: 36,
                    height: 36,
                    backgroundColor: AppStyles.colorSet.brandPrimary,
                    borderRadius: 18,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={decrementImage}
                >
                  <Feather name="chevron-left" size={20} color="#000" />
                </Pressable>

                <Pressable
                  style={{
                    width: 36,
                    height: 36,
                    backgroundColor: AppStyles.colorSet.brandPrimary,
                    borderRadius: 18,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={incrementImage}
                >
                  <Feather name="chevron-right" size={20} color="#000" />
                </Pressable>
              </View>
            )}

            {props?.allowDeletion && (
              <Pressable
                style={{
                  width: 36,
                  height: 36,
                  marginLeft: 8,
                  backgroundColor: AppStyles.colorSet.error,
                  borderRadius: 18,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onPress={deleteImage}
              >
                <Feather name="trash-2" size={18} color="#FFF" />
              </Pressable>
            )}
          </View>

          <Pressable onPress={() => setModalVisible(true)}>
            <View style={{ margin: 16, height: hp(50) }}>{getDisplayImage(galleryIndex)}</View>
          </Pressable>

          <Pressable onPress={() => setModalVisible(false)}>
            <Modal
              visible={modalVisible}
              transparent={true}
              onRequestClose={() => setModalVisible(false)}
            >
              <ImageViewer
                enablePreload={true}
                index={galleryIndex}
                imageUrls={props.images.map((x) => ({ url: x }))}
                enableSwipeDown={false}
                renderImage={renderImage}
                loadingRender={renderLoading}
                saveToLocalByLongPress={false}
              />
            </Modal>
          </Pressable>
        </View>
      )
    );
  } else {
    return (
      props?.images &&
      props?.images.length > 0 && (
        <View style={{ borderRadius: 16, borderColor: AppStyles.colorSet.cardBorder, borderWidth: 1, marginTop: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.04, shadowRadius: 8, elevation: 2, backgroundColor: AppStyles.colorSet.cardBackground }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12 }}>
            <CustomText
              font="medium"
              size={14}
              color={AppStyles.colorSet.textSecondary}
            >
              {t('gallery.indexOfTotal', { current: galleryIndex + 1, total: props.images.length })}
            </CustomText>

            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {props.images.length > 1 && (
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Pressable
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: AppStyles.colorSet.brandPrimary,
                      borderRadius: 18,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    onPress={decrementImage}
                  >
                    <Feather name="chevron-left" size={20} color="#000" />
                  </Pressable>

                  <Pressable
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: AppStyles.colorSet.brandPrimary,
                      borderRadius: 18,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    onPress={incrementImage}
                  >
                    <Feather name="chevron-right" size={20} color="#000" />
                  </Pressable>
                </View>
              )}

              {props?.allowDeletion && (
                <Pressable
                  style={{
                    width: 36,
                    height: 36,
                    marginLeft: 8,
                    backgroundColor: AppStyles.colorSet.error,
                    borderRadius: 18,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={deleteImage}
                >
                  <Feather name="trash-2" size={18} color="#FFF" />
                </Pressable>
              )}
            </View>
          </View>

          <Pressable onPress={() => setModalVisible(true)}>
            <View style={{ margin: 16, height: hp(50) }}>{getDisplayImage(galleryIndex)}</View>
          </Pressable>

          <Modal
            visible={modalVisible}
            transparent={true}
            onRequestClose={() => setModalVisible(false)}
          >
            <TouchableWithoutFeedback onPress={() => setModalVisible(false)}>
              <ImageViewer
                enablePreload={true}
                index={galleryIndex}
                imageUrls={props.images.map((x) => ({ url: x }))}
                useNativeDriver={true}
                enableSwipeDown={false}
                renderImage={renderImage}
                loadingRender={renderLoading}
                saveToLocalByLongPress={false}
              />
            </TouchableWithoutFeedback>
          </Modal>
        </View>
      )
    );
  }
};

export default MonetaImageGallery;
